import { DialogComponent } from '@theme/dialog';
import { CartAddEvent, CartErrorEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { guardATC } from './wroe-atc-guard.js';
import { PROFANITY_LIST } from './wroe-profanity-list.js';

const PET_NAME_PATTERN = /^[a-zA-Z]+$/;

/**
 * @typedef {'customize' | 'review'} WroeStepId
 */

/**
 * @param {string | undefined} value
 * @returns {value is WroeStepId}
 */
function isWroeStepId(value) {
  return value === 'customize'
    || value === 'review';
}

/**
 * @typedef {object} ThemeGlobal
 * @property {{ cart_add_url?: string }=} routes
 * @property {{ added?: string }=} translations
 */

/**
 * @typedef {{ init: () => Promise<void>, refresh: () => void, setSide: (side: 'front' | 'back') => void, updateColor: (colorHex: string) => void | Promise<void>, updateFabric: (type: string, url: string) => void | Promise<void>, updatePetName: (text: string) => void, toBlob: () => Promise<Blob | null>, destroy: () => void }} WroePreviewEngineLike
 */

/**
 * @typedef {object} WroeRefs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLInputElement} petNameInput
 * @property {HTMLButtonElement} addToCartButton
 * @property {HTMLElement} errorMessage
 * @property {HTMLElement} successMessage
 * @property {HTMLElement} liveRegion
 * @property {HTMLElement} charCounter
 * @property {HTMLCanvasElement} previewCanvas
 * @property {HTMLButtonElement} previewSideChip
 * @property {HTMLElement} previewSkeleton
 * @property {HTMLElement} stepCounter
 * @property {HTMLElement} stepProgress
 * @property {HTMLElement} stepDescription
 * @property {HTMLElement} contentScroll
 * @property {HTMLButtonElement} backBtn
 * @property {HTMLButtonElement} nextBtn
 * @property {HTMLElement} reviewSummary
 * @property {HTMLButtonElement} shareBtn
 * @property {HTMLElement} priceDisplay
 *
 * @extends {DialogComponent}
 */
class WroePersonalizationComponent extends DialogComponent {
  requiredRefs = [
    'dialog', 'petNameInput', 'addToCartButton', 'errorMessage',
    'successMessage', 'liveRegion', 'charCounter',
    'previewCanvas', 'previewSideChip', 'previewSkeleton', 'stepCounter', 'stepProgress', 'stepDescription', 'contentScroll',
    'backBtn', 'nextBtn', 'reviewSummary', 'shareBtn',
  ];

  #abortController = new AbortController();

  /** @type {Window & { Theme?: ThemeGlobal }} */
  #windowWithTheme = /** @type {Window & { Theme?: ThemeGlobal }} */ (window);

  /**
   * Saved reference to parent's closeDialog so our animated override can call it.
   * @type {(() => Promise<void>) | null}
   */
  #parentCloseDialog = null;

  /** @type {WroePreviewEngineLike | null} */
  #previewEngine = null;

  /** @type {string} */
  #selectedVariantId = '';
  /** @type {string} */
  #selectedVariantPrice = '';
  /** @type {boolean} */
  #variantAvailable = false;

  /** @type {{ petName: string, embroideryColor: string, embroideryColorImageUrl: string, frontFabric: string, frontFabricImageUrl: string, backFabric: string, backFabricImageUrl: string }} */
  #state = {
    petName: '',
    embroideryColor: '',
    embroideryColorImageUrl: '',
    frontFabric: '',
    frontFabricImageUrl: '',
    backFabric: '',
    backFabricImageUrl: '',
  };

  /** @type {WroeStepId[]} */
  #activeSteps = [];
  /** @type {number} */
  #currentStepIndex = 0;
  /** @type {Set<WroeStepId>} */
  #completedSteps = new Set();
  /** @type {Map<string, string>} */
  #sampledColorCache = new Map();
  /** @type {boolean} */
  #isDirty = false;
  /** @type {boolean} */
  #isSubmitting = false;
  /** @type {HTMLElement[]} */
  #carouselTracks = [];
  /** @type {number | null} */
  #previewPulseTimeout = null;
  /** @type {number | null} */
  #previewFlipSwapTimeout = null;
  /** @type {number | null} */
  #previewFlipEndTimeout = null;
  /** @type {'front' | 'back'} */
  #previewSide = 'front';
  /** @type {number | null} */
  #railPointerId = null;
  /** @type {number} */
  #railStartX = 0;
  /** @type {number} */
  #railStartY = 0;
  /** @type {boolean} */
  #railIsDragging = false;
  /** @type {number} */
  #lastRailDragAt = 0;
  /** @type {string} */
  #railPointerType = '';
  /** @type {boolean} */
  #resetOnNextOpen = false;
  /** @type {HTMLElement | null} */
  #lastFocusedElement = null;
  /** @type {string} */
  #mobileActiveGroup = 'petName';

  /* ═══════════════════════════════════════════
     Lifecycle
     ═══════════════════════════════════════════ */

  connectedCallback() {
    this.#parentCloseDialog = this.closeDialog;
    this.closeDialog = this.#animatedCloseDialog;

    super.connectedCallback();

    // Parse step config
    this.#activeSteps = ['customize', 'review'];

    // Do not force a narrow modal based on step count. Layout is responsive via CSS breakpoints.

    this.#syncVariantFromForm();
    this.#initCarousels();
    this.#initMobileAccordion();
    this.#syncPetNameInputFromState();
    this.#updateGroupSummaries();
    this.#showStep(0);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    if (this.#previewPulseTimeout !== null) {
      window.clearTimeout(this.#previewPulseTimeout);
      this.#previewPulseTimeout = null;
    }
    if (this.#previewFlipSwapTimeout !== null) {
      window.clearTimeout(this.#previewFlipSwapTimeout);
      this.#previewFlipSwapTimeout = null;
    }
    if (this.#previewFlipEndTimeout !== null) {
      window.clearTimeout(this.#previewFlipEndTimeout);
      this.#previewFlipEndTimeout = null;
    }
    this.#previewEngine?.destroy();
  }

  /* ═══════════════════════════════════════════
     Dialog override — lazy-load preview engine
     ═══════════════════════════════════════════ */

  showDialog() {
    this.#lastFocusedElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.#clearFeedback();
    this.#syncVariantFromForm();
    this.#completedSteps.clear();
    this.#isDirty = false;
    this.#isSubmitting = false;
    this.#clearUnavailableSelections();
    this.#setPreviewSide('front', false);
    if (this.#resetOnNextOpen) {
      this.#resetCustomization();
      this.#resetOnNextOpen = false;
    }
    this.#syncPetNameInputFromState();
    void this.#applyDefaultSelections();
    this.#setMobileAccordionGroup('petName');
    this.#showStep(0);

    super.showDialog();
    this.#focusModalTitle();
    void this.#ensurePreviewReady();
  }

  async #ensurePreviewReady(attempt = 0) {
    const dialog = /** @type {HTMLDialogElement} */ (this.refs.dialog);
    const canvas = /** @type {HTMLCanvasElement} */ (this.refs.previewCanvas);
    const rect = canvas.getBoundingClientRect();
    const hasVisibleCanvas = dialog.open && rect.width > 2 && rect.height > 2;

    if (!hasVisibleCanvas) {
      if (attempt >= 10) {
        this.#showStep(this.#currentStepIndex);
        this.#refreshCarouselButtons();
        return;
      }

      await new Promise((resolve) => window.requestAnimationFrame(resolve));
      return this.#ensurePreviewReady(attempt + 1);
    }

    this.#showStep(this.#currentStepIndex);
    this.#refreshCarouselButtons();
    await this.#initPreviewEngine();
    this.#previewEngine?.refresh();
  }

  async #initPreviewEngine() {
    if (this.#previewEngine) return;

    const engineUrl = this.dataset.previewEngineUrl;
    if (!engineUrl) return;

    const configEl = this.#previewConfigElement;
    if (!configEl) return;

    try {
      const dynamicImport = /** @type {(url: string) => Promise<{ WroePreviewEngine: new (canvas: HTMLCanvasElement, config: unknown) => WroePreviewEngineLike }>} */ (
        new Function('url', 'return import(url);')
      );
      const mod = await dynamicImport(engineUrl);
      const configText = configEl.textContent || '{}';
      const config = JSON.parse(configText);
      const canvas = /** @type {HTMLCanvasElement} */ (this.refs.previewCanvas);
      const previewEngine = new mod.WroePreviewEngine(canvas, config);
      await previewEngine.init();
      this.#previewEngine = previewEngine;
      this.#previewEngine.setSide?.(this.#previewSide);
      await this.#syncPreviewFromState();
      this.#previewEngine.refresh?.();
      const skeleton = /** @type {HTMLElement} */ (this.refs.previewSkeleton);
      skeleton.classList.add('hidden');
    } catch (err) {
      console.error('Preview engine failed to load:', err);
      const skeleton = /** @type {HTMLElement} */ (this.refs.previewSkeleton);
      skeleton.classList.add('hidden');
    }
  }

  /* ═══════════════════════════════════════════
     Step navigation
     ═══════════════════════════════════════════ */

  /**
   * @param {number} index
   */
  #showStep(index) {
    if (index < 0 || index >= this.#activeSteps.length) return;
    this.#currentStepIndex = index;
    const currentStep = this.#activeSteps[index];
    if (!currentStep) return;

    const contentScroll = /** @type {HTMLElement} */ (this.refs.contentScroll);
    contentScroll.scrollTop = 0;

    // Hide all panels, show active
    /** @type {NodeListOf<HTMLElement>} */
    const panels = this.querySelectorAll('[data-wroe-step-panel]');
    for (const panel of panels) {
      panel.classList.toggle('is-active', panel.dataset.wroeStepPanel === currentStep);
    }

    // Update step bar segments
    /** @type {NodeListOf<HTMLElement>} */
    const stepBtns = this.querySelectorAll('.wroe-step-bar__segment');
    for (const btn of stepBtns) {
      const step = btn.dataset.wroeStep;
      btn.classList.toggle('is-active', step === currentStep);
      btn.classList.toggle('is-completed', isWroeStepId(step) && this.#completedSteps.has(step));
      btn.toggleAttribute('aria-current', step === currentStep);
    }

    // Hide step meta on both customize (no progress needed) and review (Phase 5: clean confirmation page)
    const stepProgress = /** @type {HTMLElement} */ (this.refs.stepProgress);
    const stepDescription = /** @type {HTMLElement} */ (this.refs.stepDescription);
    const stepMeta = stepProgress.parentElement;
    stepProgress.textContent = '';
    stepDescription.textContent = '';
    if (stepMeta) stepMeta.classList.add('is-hidden');

    // Back button visibility
    const backBtn = /** @type {HTMLElement} */ (this.refs.backBtn);
    backBtn.classList.toggle('hidden', currentStep !== 'review');

    // Next button label + visibility
    const nextBtn = /** @type {HTMLElement} */ (this.refs.nextBtn);
    const isLastStep = currentStep === 'review';
    nextBtn.classList.toggle('hidden', isLastStep);
    const atcBtn = /** @type {HTMLElement} */ (this.refs.addToCartButton);
    atcBtn.classList.toggle('hidden', !isLastStep);

    // Next button label is set in HTML (includes SVG arrow), no need to update text dynamically
    // for a 2-step flow. If more steps are added, update the first child text node here.

    // Populate review when entering review step
    if (currentStep === 'review') {
      this.#populateReview();
      this.#updateAddToCartState();
    }

    this.#syncCurrentStepControls(currentStep);
    this.#updateGroupCompleteStates();
    this.#refreshCarouselButtons();
  }

  /**
   * @param {WroeStepId} step
   */
  #syncCurrentStepControls(step) {
    const nextBtn = /** @type {HTMLButtonElement} */ (this.refs.nextBtn);
    if (step === 'review') {
      nextBtn.disabled = true;
      this.#updateAddToCartState();
      return;
    }

    nextBtn.disabled = !this.#isStepComplete(step);
    this.#updateAddToCartState();
  }

  #refreshCurrentStepControls() {
    const currentStep = this.#activeSteps[this.#currentStepIndex];
    if (!currentStep) return;
    this.#syncCurrentStepControls(currentStep);
  }

  /**
   * @param {Event} event
   */
  handleNextStep = (event) => {
    event.preventDefault();
    const currentStep = this.#activeSteps[this.#currentStepIndex];
    if (!currentStep) return;

    if (!this.#validateStep(currentStep)) return;

    this.#completedSteps.add(currentStep);
    this.#showStep(this.#currentStepIndex + 1);
  };

  /**
   * @param {Event} event
   */
  prevStep = (event) => {
    event.preventDefault();
    this.#showStep(this.#currentStepIndex - 1);
  };

  /**
   * @param {Event} event
   */
  goToStep = (event) => {
    event.preventDefault();
    const target = event.target;
    if (!(target instanceof Element)) return;
    const btn = target.closest('.wroe-step-bar__segment');
    if (!(btn instanceof HTMLElement)) return;

    const targetStep = btn.dataset.wroeStep;
    if (!isWroeStepId(targetStep)) return;
    const targetIndex = this.#activeSteps.indexOf(targetStep);

    if (targetIndex < 0) return;
    if (targetIndex > this.#currentStepIndex) {
      // Allow jumping forward only when current step is complete.
      const current = this.#activeSteps[this.#currentStepIndex];
      if (!current) return;
      if (!this.#isStepComplete(current)) return;
    }

    this.#showStep(targetIndex);
  };

  /**
   * @param {WroeStepId} step
   * @returns {boolean}
   */
  #validateStep(step) {
    if (this.#isStepComplete(step)) {
      this.#clearFeedback();
      return true;
    }

    switch (step) {
      case 'customize':
        if (this.#hasColorOptions && !this.#state.embroideryColor) {
          this.#setError('Please select an embroidery color.', 'embroideryColor');
          return false;
        }
        if (this.#hasFrontFabricOptions && !this.#state.frontFabric) {
          this.#setError('Please select a front fabric.', 'frontFabric');
          return false;
        }
        if (this.#hasBackFabricOptions && !this.#state.backFabric) {
          this.#setError('Please select a back fabric.', 'backFabric');
          return false;
        }
        if (!this.#state.petName) {
          this.#setError('Please enter a pet name.', 'petName');
          return false;
        }
        if (!PET_NAME_PATTERN.test(this.#state.petName)) {
          this.#setError('Letters only — no spaces or numbers.', 'petName');
          return false;
        }
        if (this.#isPetNameProfane(this.#state.petName)) {
          this.#setError('Please choose a different pet name.', 'petName');
          return false;
        }
        return true;
      case 'review':
        return true;
    }

    return false;
  }

  /**
   * @param {WroeStepId} step
   * @returns {boolean}
   */
  #isStepComplete(step) {
    switch (step) {
      case 'customize':
        return (
          (!this.#hasColorOptions || this.#state.embroideryColor.length > 0) &&
          (!this.#hasFrontFabricOptions || this.#state.frontFabric.length > 0) &&
          (!this.#hasBackFabricOptions || this.#state.backFabric.length > 0) &&
          this.#state.petName.length > 0 &&
          PET_NAME_PATTERN.test(this.#state.petName) &&
          !this.#isPetNameProfane(this.#state.petName)
        );
      case 'review':
        return true;
    }
  }

  /**
   * @param {string} value
   * @returns {boolean}
   */
  #isPetNameProfane(value) {
    const normalized = value.trim().toLowerCase();
    if (!normalized) return false;
    if (PROFANITY_LIST.has(normalized)) return true;

    for (const term of PROFANITY_LIST) {
      if (term.length >= 4 && normalized.includes(term)) {
        return true;
      }
    }
    return false;
  }

  /* ═══════════════════════════════════════════
     Selection handlers
     ═══════════════════════════════════════════ */

  /**
   * @param {Event} event
   */
  selectColor = async (event) => {
    if (!this.#hasColorOptions) return;
    if (this.#shouldIgnoreSelection(event)) return;
    const target = event.target;
    if (!(target instanceof Element)) return;
    const el = target.closest('[data-wroe-select="embroideryColor"]');
    if (!(el instanceof HTMLElement)) return;

    const value = el.dataset.value || '';
    if (!value) return;
    if (el.classList.contains('is-selected')) {
      this.#clearSelection('embroideryColor');
      return;
    }

    this.#state.embroideryColor = value;
    this.#state.embroideryColorImageUrl = el.dataset.imageUrl || '';
    this.#isDirty = true;
    this.#updateSelectionUI('embroideryColor', value);
    const previewColor = await this.#resolvePreviewColor(el.dataset.previewColor || value, el.dataset.imageUrl || '');
    this.#previewEngine?.updateColor(previewColor);
    this.#pulsePreview();
    this.#clearFeedback();
    this.#updateGroupCompleteStates();
    this.#refreshCurrentStepControls();
  };

  /**
   * @param {Event} event
   */
  selectFabric = (event) => {
    if (this.#shouldIgnoreSelection(event)) return;
    const target = event.target;
    if (!(target instanceof Element)) return;
    const el = target.closest('[data-wroe-select]');
    if (!(el instanceof HTMLElement)) return;

    const value = el.dataset.value || '';
    const field = el.dataset.wroeSelect || '';
    const imageUrl = el.dataset.imageUrl || '';
    if (!value || !field) return;
    if (!this.#isOptionEnabled(field)) return;
    if (el.classList.contains('is-selected')) {
      this.#clearSelection(field);
      return;
    }

    if (field === 'frontFabric') {
      this.#state.frontFabric = value;
      this.#state.frontFabricImageUrl = imageUrl;
      this.#setPreviewSide('front');
    }
    if (field === 'backFabric') {
      this.#state.backFabric = value;
      this.#state.backFabricImageUrl = imageUrl;
      this.#setPreviewSide('back');
    }
    this.#isDirty = true;
    this.#updateSelectionUI(field, value);

    const fabricType = field === 'frontFabric' ? 'front' : 'back';
    this.#previewEngine?.updateFabric(fabricType, imageUrl);
    this.#pulsePreview();
    this.#clearFeedback();
    this.#updateGroupCompleteStates();
    this.#refreshCurrentStepControls();
  };

  /**
   * @param {Event} event
   */
  handlePetNameInput = (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;

    const maxLength = this.#maxPetNameLength;
    const sanitized = event.target.value.replace(/[^a-zA-Z]/g, '').toUpperCase().slice(0, maxLength);
    event.target.value = sanitized;
    if (sanitized) {
      event.target.setAttribute('value', sanitized);
    } else {
      event.target.removeAttribute('value');
    }
    this.#state.petName = sanitized;
    this.#isDirty = sanitized.length > 0 || this.#isDirty;
    this.#updateCharCounter();
    this.#previewEngine?.updatePetName(sanitized);
    this.#pulsePreview();
    if (!sanitized) {
      this.#clearFeedback();
    } else if (this.#isPetNameProfane(sanitized)) {
      this.#setError('Please choose a different pet name.');
    } else {
      this.#clearFeedback();
    }
    this.#updateGroupCompleteStates();
    this.#refreshCurrentStepControls();
  };

  /**
   * @param {Event} event
   */
  togglePreviewSide = (event) => {
    event.preventDefault();
    const next = this.#previewSide === 'front' ? 'back' : 'front';
    this.#setPreviewSide(next);
  };

  #updateGroupCompleteStates() {
    const groups = this.querySelectorAll('[data-wroe-group]');
    for (const el of groups) {
      if (!(el instanceof HTMLElement)) continue;
      const key = el.dataset.wroeGroup || '';
      let isComplete = false;

      if (key === 'embroideryColor') isComplete = this.#state.embroideryColor.length > 0;
      if (key === 'frontFabric') isComplete = this.#state.frontFabric.length > 0;
      if (key === 'backFabric') isComplete = this.#state.backFabric.length > 0;
      if (key === 'petName') {
        isComplete =
          this.#state.petName.length > 0 &&
          PET_NAME_PATTERN.test(this.#state.petName) &&
          !this.#isPetNameProfane(this.#state.petName);
      }

      el.classList.toggle('is-complete', isComplete);
    }
    this.#updateGroupSummaries();
  }

  #initMobileAccordion() {
    window.addEventListener('resize', () => this.#syncMobileAccordionState(), { signal: this.#abortController.signal });
    this.#syncMobileAccordionState();
  }

  #syncMobileAccordionState() {
    const groups = this.querySelectorAll('[data-wroe-group]');
    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    for (const group of groups) {
      if (!(group instanceof HTMLElement)) continue;
      const key = group.dataset.wroeGroup || '';
      const toggle = group.querySelector('[data-wroe-group-toggle]');
      if (toggle instanceof HTMLButtonElement) {
        const expanded = isMobile ? true : key === this.#mobileActiveGroup;
        toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      }
      group.classList.toggle('is-collapsed', !isMobile && key !== this.#mobileActiveGroup);
    }
  }

  /**
   * @param {string} group
   */
  #setMobileAccordionGroup(group) {
    this.#mobileActiveGroup = group;
    this.#syncMobileAccordionState();
  }

  /**
   * @param {Event} event
   */
  toggleGroup = (event) => {
    event.preventDefault();
    if (window.matchMedia('(max-width: 749px)').matches) return;
    const target = event.target;
    if (!(target instanceof Element)) return;
    const btn = target.closest('[data-wroe-group-toggle]');
    if (!(btn instanceof HTMLElement)) return;
    const group = btn.dataset.wroeGroupToggle || '';
    if (!group) return;
    this.#setMobileAccordionGroup(group);
  };

  #updateGroupSummaries() {
    const summaries = this.querySelectorAll('[data-wroe-group-summary]');
    for (const node of summaries) {
      if (!(node instanceof HTMLElement)) continue;
      const key = node.dataset.wroeGroupSummary || '';
      switch (key) {
        case 'petName':
          node.textContent = this.#state.petName || 'Not selected';
          break;
        case 'embroideryColor':
          node.textContent = this.#state.embroideryColor || 'Not selected';
          break;
        case 'frontFabric':
          node.textContent = this.#state.frontFabric || 'Not selected';
          break;
        case 'backFabric':
          node.textContent = this.#state.backFabric || 'Not selected';
          break;
        default:
          node.textContent = 'Not selected';
      }
    }

    /** @type {NodeListOf<HTMLElement>} */
    const swatches = this.querySelectorAll('[data-wroe-group-swatch]');
    for (const swatch of swatches) {
      const key = swatch.dataset.wroeGroupSwatch || '';
      let imageUrl = '';
      if (key === 'embroideryColor') imageUrl = this.#state.embroideryColorImageUrl;
      if (key === 'frontFabric') imageUrl = this.#state.frontFabricImageUrl;
      if (key === 'backFabric') imageUrl = this.#state.backFabricImageUrl;

      if (swatch instanceof HTMLImageElement) {
        swatch.classList.toggle('hidden', !imageUrl);
        if (imageUrl) {
          swatch.src = imageUrl;
          swatch.alt = '';
        } else {
          swatch.removeAttribute('src');
          swatch.alt = '';
        }
      }
    }
  }

  /**
   * @param {Event} event
   */
  handleLauncherClick = (event) => {
    event.preventDefault();
    this.showDialog();
  };

  /**
   * @param {string} candidate
   * @param {string} imageUrl
   * @returns {Promise<string>}
   */
  async #resolvePreviewColor(candidate, imageUrl) {
    const normalized = candidate.trim();
    if (this.#isCssColor(normalized)) return normalized;
    if (!imageUrl) return '#000000';

    const sampled = await this.#sampleColorFromImage(imageUrl);
    return sampled || '#000000';
  }

  /**
   * @param {string} value
   * @returns {boolean}
   */
  #isCssColor(value) {
    return Boolean(value) && typeof CSS !== 'undefined' && CSS.supports('color', value);
  }

  /**
   * @param {string} imageUrl
   * @returns {Promise<string>}
   */
  async #sampleColorFromImage(imageUrl) {
    const cached = this.#sampledColorCache.get(imageUrl);
    if (cached) return cached;

    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          if (!ctx) return resolve('#000000');

          const size = 12;
          canvas.width = size;
          canvas.height = size;
          ctx.drawImage(img, 0, 0, size, size);
          const data = ctx.getImageData(0, 0, size, size).data;

          let r = 0;
          let g = 0;
          let b = 0;
          let count = 0;

          for (let i = 0; i < data.length; i += 4) {
            const rVal = data[i];
            const gVal = data[i + 1];
            const bVal = data[i + 2];
            const aVal = data[i + 3];

            if (rVal === undefined || gVal === undefined || bVal === undefined || aVal === undefined) continue;
            if (aVal < 20) continue;

            r += rVal;
            g += gVal;
            b += bVal;
            count += 1;
          }

          if (count === 0) return resolve('#000000');
          const color = `rgb(${Math.round(r / count)}, ${Math.round(g / count)}, ${Math.round(b / count)})`;
          this.#sampledColorCache.set(imageUrl, color);
          resolve(color);
        } catch {
          resolve('#000000');
        }
      };
      img.onerror = () => resolve('#000000');
      img.src = imageUrl;
    });
  }

  /* ═══════════════════════════════════════════
     Review step
     ═══════════════════════════════════════════ */

  #populateReview() {
    const container = /** @type {HTMLElement} */ (this.refs.reviewSummary);
    if (!container) return;

    /** @type {string[]} */
    const rows = [];

    if (this.#state.embroideryColor) {
      rows.push(
        this.#makeReviewRow('Color', this.#state.embroideryColor, 'customize', this.#state.embroideryColorImageUrl)
      );
    }
    if (this.#state.frontFabric) {
      rows.push(
        this.#makeReviewRow('Front Fabric', this.#state.frontFabric, 'customize', this.#state.frontFabricImageUrl)
      );
    }
    if (this.#state.backFabric) {
      rows.push(
        this.#makeReviewRow('Back Fabric', this.#state.backFabric, 'customize', this.#state.backFabricImageUrl)
      );
    }
    if (this.#state.petName) {
      rows.push(this.#makeReviewRow('Pet Name', this.#state.petName, 'customize'));
    }
    if (this.#selectedVariantPrice) {
      rows.push(this.#makeReviewRow('Price', this.#selectedVariantPrice, 'review'));
    }

    container.innerHTML = rows.join('');

    // Bind edit links
    const editBtns = container.querySelectorAll('[data-edit-step]');
    for (const btn of editBtns) {
      btn.addEventListener('click', () => {
        const step = /** @type {HTMLElement} */ (btn).dataset.editStep || '';
        if (!isWroeStepId(step)) return;
        const idx = this.#activeSteps.indexOf(step);
        if (idx >= 0) this.#showStep(idx);
      });
    }

    // Share button
    const shareBtn = /** @type {HTMLElement} */ (this.refs.shareBtn);
    shareBtn.classList.remove('hidden');
  }

  /**
   * @param {string} label
   * @param {string} value
   * @param {string} editStep
   * @param {string} [thumbUrl]
   * @returns {string}
   */
  #makeReviewRow(label, value, editStep, thumbUrl) {
    const thumb = thumbUrl
      ? `<img class="wroe-review-row__thumb" src="${this.#escapeHtml(thumbUrl)}" alt="" loading="lazy">`
      : '';

    return `
      <div class="wroe-review-row">
        <div class="wroe-review-row__main">
          ${thumb}
          <div>
            <span class="wroe-review-row__label">${label}</span>
            <span class="wroe-review-row__value">${this.#escapeHtml(value)}</span>
          </div>
        </div>
        <button type="button" class="wroe-review-row__edit" data-edit-step="${editStep}">Edit</button>
      </div>`;
  }

  /**
   * @param {string} str
   * @returns {string}
   */
  #escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  /* ═══════════════════════════════════════════
     Share
     ═══════════════════════════════════════════ */

  /**
   * @param {Event} event
   */
  sharePreview = async (event) => {
    event.preventDefault();
    if (!this.#previewEngine) return;

    try {
      const blob = await this.#previewEngine.toBlob();
      if (!blob) return;

      const productName = this.dataset.productTitle || 'personalized-product';
      const fileName = `${productName.replace(/\\s+/g, '-').toLowerCase()}-preview.png`;
      const file = new File([blob], fileName, { type: 'image/png' });

      const shareTitle = this.dataset.shareTitle || productName;
      const shareText = this.dataset.shareText || "What do you Think? It's my personalization";

      if (navigator.canShare && navigator.canShare(/** @type {ShareData} */({ files: [file] }))) {
        await navigator.share(
          /** @type {ShareData} */({
            title: shareTitle,
            text: shareText,
            files: [file],
          })
        );
        return;
      }

      // Fallback: download the image (Phase 01 Plan 04)
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      if (err instanceof Error && err.name !== 'AbortError') {
        console.error('Share failed:', err);
      }
    }
  };

  /* ═══════════════════════════════════════════
     Add to Cart — 100% preserved logic
     ═══════════════════════════════════════════ */

  /**
   * @param {Event} event
   */
  handleAddToCart = guardATC(async (/** @type {Event} */ event) => {
    event.preventDefault();
    this.#clearFeedback();
    this.#syncVariantFromForm();
    this.#updateAddToCartState();

    const atcBtn = /** @type {HTMLButtonElement} */ (this.refs.addToCartButton);
    if (atcBtn.disabled) {
      this.#setError('Please complete all required personalization fields.');
      return;
    }
    this.#isSubmitting = true;

    const formData = new FormData();
    formData.append('id', this.#selectedVariantId);
    formData.append('quantity', '1');
    formData.append('properties[Pet Name]', this.#state.petName);

    if (this.#state.embroideryColor) {
      formData.append('properties[Embroidery Color]', this.#state.embroideryColor);
      if (this.#state.embroideryColorImageUrl) {
        formData.append('properties[_wroe_embroidery_color_swatch]', this.#state.embroideryColorImageUrl);
      }
    }
    if (this.#state.frontFabric) {
      formData.append('properties[Front Fabric]', this.#state.frontFabric);
      if (this.#state.frontFabricImageUrl) {
        formData.append('properties[_wroe_front_fabric_swatch]', this.#state.frontFabricImageUrl);
      }
    }
    if (this.#state.backFabric) {
      formData.append('properties[Back Fabric]', this.#state.backFabric);
      if (this.#state.backFabricImageUrl) {
        formData.append('properties[_wroe_back_fabric_swatch]', this.#state.backFabricImageUrl);
      }
    }
    formData.append('properties[_wroe_personalization_version]', '1');

    /** @type {string[]} */
    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((element) => /** @type {HTMLElement} */(element).dataset.sectionId || '')
      .filter(Boolean);
    if (sectionIds.length > 0) {
      formData.append('sections', sectionIds.join(','));
    }

    const addUrl = this.#windowWithTheme.Theme?.routes?.cart_add_url || '/cart/add.js';
    const request = fetchConfig('javascript', { body: formData });

    try {
      const response = await fetch(addUrl, {
        ...request,
        headers: {
          ...request.headers,
          Accept: 'text/html',
        },
      });
      const payload = await response.json();

      if (payload.status) {
        this.dispatchEvent(new CartErrorEvent(this.id, payload.message, payload.description, payload.errors));
        this.#setError(payload.message || 'Unable to add this personalized product right now.');
        return;
      }

      this.dispatchEvent(
        new CartAddEvent({}, this.#selectedVariantId, {
          source: 'wroe-personalization',
          itemCount: 1,
          productId: this.dataset.productId,
          sections: payload.sections,
        })
      );

      const successMsg = /** @type {HTMLElement} */ (this.refs.successMessage);
      successMsg.textContent = 'Added to cart.';
      successMsg.classList.remove('hidden');
      const liveRegion = /** @type {HTMLElement} */ (this.refs.liveRegion);
      liveRegion.textContent = this.#windowWithTheme.Theme?.translations?.added || 'Added';

      this.#isDirty = false;
      this.#resetOnNextOpen = true;
      await this.closeDialog();

      // Trigger fly-to-cart animation on the add-to-cart-component
      const addToCartEl = /** @type {HTMLElement & { animateAddToCart?: () => void, refs?: { addToCartButton?: HTMLElement } }} */ (
        this.#productForm?.querySelector('add-to-cart-component')
      );
      if (addToCartEl) {
        const animationEnabled = addToCartEl.dataset.addToCartAnimation === 'true';
        if (animationEnabled && typeof addToCartEl.animateAddToCart === 'function') {
          const cartIcon = document.querySelector('.header-actions__cart-icon');
          const image = addToCartEl.dataset.productVariantMedia;
          if (cartIcon && image) {
            /** @type {HTMLElement & { source: Element, destination: Element }} */
            const flyToCartElement = /** @type {any} */ (document.createElement('fly-to-cart'));
            flyToCartElement.classList.add('fly-to-cart--main');
            flyToCartElement.style.setProperty('background-image', `url(${image})`);
            flyToCartElement.style.setProperty('--start-opacity', '0');
            flyToCartElement.source = addToCartEl.refs?.addToCartButton || addToCartEl;
            flyToCartElement.destination = cartIcon;
            document.body.appendChild(flyToCartElement);
          }
          addToCartEl.animateAddToCart();
        }
      }

      this.#ensureLauncherEnabled();
      // Defer reset to next open — #resetOnNextOpen (line 980) already flags it.
      // Resetting eagerly here crashes the preview engine canvas (0-size after
      // dialog close) which would skip #ensureLauncherEnabled and show an error.
    } catch (error) {
      console.error(error);
      this.#ensureLauncherEnabled();
      this.#setError('Unable to add this personalized product right now.');
    } finally {
      this.#isSubmitting = false;
    }
  });

  /* ═══════════════════════════════════════════
     Animated close (Brand-kit Rule 6) — preserved
     ═══════════════════════════════════════════ */

  #animatedCloseDialog = async () => {
    const dialog = /** @type {HTMLDialogElement} */ (this.refs.dialog);
    if (!dialog || !dialog.open) return;
    if (this.#isDirty && !this.#isSubmitting) {
      const shouldClose = window.confirm('You have unsaved personalization changes. Close anyway?');
      if (!shouldClose) return;
    }
    this.#isDirty = false;

    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) {
      return this.#parentCloseDialog?.();
    }

    dialog.classList.add('is-closing');
    await new Promise((resolve) => {
      dialog.addEventListener('animationend', resolve, { once: true });
      setTimeout(resolve, 300);
    });
    dialog.classList.remove('is-closing');
    await this.#parentCloseDialog?.();
    this.#restoreLauncherFocus();
    return;
  };

  #restoreLauncherFocus() {
    if (this.#lastFocusedElement instanceof HTMLElement) {
      this.#lastFocusedElement.focus();
    }
  }

  /* ═══════════════════════════════════════════
     Variant sync (fallback for products without Size step)
     ═══════════════════════════════════════════ */

  #syncVariantFromForm() {
    const form = this.#productForm;
    const variantInput = /** @type {HTMLInputElement | null} */ (form?.querySelector('input[name="id"]'));

    this.#selectedVariantId = variantInput?.value || this.dataset.selectedVariantId || '';

    // Derive availability from the variant ID rather than launcher disabled state.
    // After a successful cart add, section morphing can leave the launcher transiently
    // disabled, which would incorrectly block reopening the personalization modal.
    this.#variantAvailable = this.#selectedVariantId !== '';
  }

  /**
   * Re-enable the launcher button after a successful personalized add-to-cart.
   * The CartAddEvent can trigger async section updates (cart-items morph,
   * cart-quantity sync) that may transiently disable the button. Re-enable
   * both immediately and after a frame to catch deferred DOM updates.
   */
  #ensureLauncherEnabled() {
    const enable = () => {
      const form = this.#productForm;
      const launcher = /** @type {HTMLButtonElement | null} */ (form?.querySelector('[data-personalization-launcher="true"]'));
      if (launcher && this.#selectedVariantId !== '') {
        launcher.disabled = false;
      }
    };
    enable();
    requestAnimationFrame(enable);
  }

  /* ═══════════════════════════════════════════
     State helpers
     ═══════════════════════════════════════════ */

  #updateAddToCartState() {
    const hasPetName =
      this.#state.petName.length > 0 &&
      PET_NAME_PATTERN.test(this.#state.petName) &&
      !this.#isPetNameProfane(this.#state.petName);
    const hasColor = !this.#hasColorOptions || this.#state.embroideryColor.length > 0;
    const hasFrontFabric = !this.#hasFrontFabricOptions || this.#state.frontFabric.length > 0;
    const hasBackFabric = !this.#hasBackFabricOptions || this.#state.backFabric.length > 0;

    const canSubmit =
      this.#variantAvailable &&
      this.#selectedVariantId.length > 0 &&
      hasPetName &&
      hasColor &&
      hasFrontFabric &&
      hasBackFabric;

    const atcBtn = /** @type {HTMLButtonElement} */ (this.refs.addToCartButton);
    atcBtn.disabled = !canSubmit;
  }

  #clearUnavailableSelections() {
    if (!this.#hasColorOptions) {
      this.#state.embroideryColor = '';
      this.#state.embroideryColorImageUrl = '';
      this.#previewEngine?.updateColor('#000000');
      this.#updateSelectionUI('embroideryColor', '');
    }

    if (!this.#hasFrontFabricOptions) {
      this.#state.frontFabric = '';
      this.#state.frontFabricImageUrl = '';
      this.#previewEngine?.updateFabric('front', '');
      this.#updateSelectionUI('frontFabric', '');
    }

    if (!this.#hasBackFabricOptions) {
      this.#state.backFabric = '';
      this.#state.backFabricImageUrl = '';
      this.#previewEngine?.updateFabric('back', '');
      this.#updateSelectionUI('backFabric', '');
    }
  }

  /**
   * @param {string} field
   * @param {string} value
   */
  #updateSelectionUI(field, value) {
    if (!this.#isOptionEnabled(field)) return;
    const selector = `[data-wroe-select="${field}"]`;
    const options = this.querySelectorAll(selector);

    for (const option of options) {
      if (!(option instanceof HTMLElement)) continue;
      option.classList.toggle('is-selected', option.dataset.value === value);
    }
  }

  /**
   * @param {string} message
   * @param {string} [focusGroup] - optional data-wroe-group value to scroll into view
   */
  #setError(message, focusGroup) {
    const successMsg = /** @type {HTMLElement} */ (this.refs.successMessage);
    successMsg.classList.add('hidden');
    const errorMsg = /** @type {HTMLElement} */ (this.refs.errorMessage);
    errorMsg.textContent = message;
    errorMsg.classList.remove('hidden');
    const liveRegion = /** @type {HTMLElement} */ (this.refs.liveRegion);
    liveRegion.textContent = message;

    // Scroll the incomplete group into view if specified
    if (focusGroup) {
      const group = this.querySelector(`[data-wroe-group="${focusGroup}"]`);
      group?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  #clearFeedback() {
    const errorMsg = /** @type {HTMLElement} */ (this.refs.errorMessage);
    errorMsg.textContent = '';
    errorMsg.classList.add('hidden');
    const successMsg = /** @type {HTMLElement} */ (this.refs.successMessage);
    successMsg.textContent = '';
    successMsg.classList.add('hidden');
    const liveRegion = /** @type {HTMLElement} */ (this.refs.liveRegion);
    liveRegion.textContent = '';
  }

  #updateCharCounter() {
    const current = this.#state.petName.length;
    const max = this.#maxPetNameLength;
    const counter = /** @type {HTMLElement} */ (this.refs.charCounter);
    counter.textContent = `${current}/${max}`;

    const mobileCounters = this.querySelectorAll('[data-wroe-mobile-char-counter]');
    for (const node of mobileCounters) {
      if (!(node instanceof HTMLElement)) continue;
      node.textContent = `${current}/${max} characters`;
    }
  }

  #syncPetNameInputFromState() {
    const nameInput = /** @type {HTMLInputElement} */ (this.refs.petNameInput);
    if (nameInput.value !== this.#state.petName) {
      nameInput.value = this.#state.petName;
    }
    if (this.#state.petName) {
      nameInput.setAttribute('value', this.#state.petName);
    } else {
      nameInput.removeAttribute('value');
    }
    this.#updateCharCounter();
  }

  #initCarousels() {
    this.#carouselTracks = Array.from(this.querySelectorAll('[data-wroe-carousel-track]'))
      .filter((node) => node instanceof HTMLElement);

    for (const track of this.#carouselTracks) {
      track.addEventListener('scroll', () => this.#updateCarouselButtons(track), { signal: this.#abortController.signal });
      this.#bindRailGestureGuards(track);
    }

    window.addEventListener('resize', () => this.#refreshCarouselButtons(), { signal: this.#abortController.signal });
    this.#refreshCarouselButtons();
  }

  /**
   * Prevent tap-selection during horizontal drag gestures on swatch rails.
   * @param {HTMLElement} track
   */
  #bindRailGestureGuards(track) {
    void track;
  }

  /**
   * @param {Event} event
   * @returns {boolean}
   */
  #shouldIgnoreSelection(event) {
    void event;
    return false;
  }

  /**
   * @param {string} field
   */
  #clearSelection(field) {
    if (!this.#isOptionEnabled(field)) return;
    switch (field) {
      case 'embroideryColor': {
        this.#state.embroideryColor = '';
        this.#state.embroideryColorImageUrl = '';
        this.#previewEngine?.updateColor('#000000');
        break;
      }
      case 'frontFabric': {
        this.#state.frontFabric = '';
        this.#state.frontFabricImageUrl = '';
        this.#previewEngine?.updateFabric('front', '');
        break;
      }
      case 'backFabric': {
        this.#state.backFabric = '';
        this.#state.backFabricImageUrl = '';
        this.#previewEngine?.updateFabric('back', '');
        break;
      }
      default:
        return;
    }
    this.#updateSelectionUI(field, '');
    this.#pulsePreview();
    this.#clearFeedback();
    this.#updateGroupCompleteStates();
    this.#refreshCurrentStepControls();
  }

  clearGroup = (/** @type {Event} */ event) => {
    const button = /** @type {HTMLElement | null} */ (event.currentTarget);
    const group = button?.dataset?.wroeClearGroup;
    if (!group) return;
    this.#clearSelection(group);
  };

  #focusModalTitle() {
    const title = this.querySelector('[id^="wroePersonalizationTitle-"]');
    if (title instanceof HTMLElement) {
      title.focus();
    }
  }

  async #syncPreviewFromState() {
    this.#syncPetNameInputFromState();
    if (!this.#previewEngine) return;

    if (this.#state.embroideryColor) {
      let colorBtn = null;
      const colorBtns = this.querySelectorAll('[data-wroe-select="embroideryColor"]');
      for (const node of colorBtns) {
        if (!(node instanceof HTMLElement)) continue;
        if ((node.dataset.value || '') === this.#state.embroideryColor) {
          colorBtn = node;
          break;
        }
      }
      if (colorBtn instanceof HTMLElement) {
        const resolvedColor = await this.#resolvePreviewColor(
          colorBtn.dataset.previewColor || this.#state.embroideryColor,
          colorBtn.dataset.imageUrl || ''
        );
        this.#previewEngine.updateColor(resolvedColor);
      }
    }
    if (this.#state.frontFabricImageUrl) {
      this.#previewEngine.updateFabric('front', this.#state.frontFabricImageUrl);
    }
    if (this.#state.backFabricImageUrl) {
      this.#previewEngine.updateFabric('back', this.#state.backFabricImageUrl);
    }
    this.#previewEngine.updatePetName(this.#state.petName);
  }

  async #applyDefaultSelections() {
    let mutated = false;

    const firstColor = this.#hasColorOptions
      ? this.querySelector('[data-wroe-select="embroideryColor"]')
      : null;
    if (!this.#state.embroideryColor && firstColor instanceof HTMLElement) {
      const value = firstColor.dataset.value || '';
      if (value) {
        this.#state.embroideryColor = value;
        this.#state.embroideryColorImageUrl = firstColor.dataset.imageUrl || '';
        this.#updateSelectionUI('embroideryColor', value);
        mutated = true;
      }
    }

    const firstFront = this.#hasFrontFabricOptions
      ? this.querySelector('[data-wroe-select="frontFabric"]')
      : null;
    if (!this.#state.frontFabric && firstFront instanceof HTMLElement) {
      const value = firstFront.dataset.value || '';
      if (value) {
        this.#state.frontFabric = value;
        this.#state.frontFabricImageUrl = firstFront.dataset.imageUrl || '';
        this.#updateSelectionUI('frontFabric', value);
        mutated = true;
      }
    }

    const firstBack = this.#hasBackFabricOptions
      ? this.querySelector('[data-wroe-select="backFabric"]')
      : null;
    if (!this.#state.backFabric && firstBack instanceof HTMLElement) {
      const value = firstBack.dataset.value || '';
      if (value) {
        this.#state.backFabric = value;
        this.#state.backFabricImageUrl = firstBack.dataset.imageUrl || '';
        this.#updateSelectionUI('backFabric', value);
        mutated = true;
      }
    }

    if (!mutated) return;
    await this.#syncPreviewFromState();
    this.#updateGroupCompleteStates();
    this.#refreshCurrentStepControls();
    this.#refreshCarouselButtons();
  }

  #resetCustomization() {
    this.#state.petName = '';
    this.#state.embroideryColor = '';
    this.#state.embroideryColorImageUrl = '';
    this.#state.frontFabric = '';
    this.#state.frontFabricImageUrl = '';
    this.#state.backFabric = '';
    this.#state.backFabricImageUrl = '';

    this.#syncPetNameInputFromState();

    const options = this.querySelectorAll('[data-wroe-select]');
    for (const option of options) {
      if (!(option instanceof HTMLElement)) continue;
      option.classList.remove('is-selected');
    }

    this.#previewEngine?.updateColor('#000000');
    this.#previewEngine?.updateFabric('front', '');
    this.#previewEngine?.updateFabric('back', '');
    this.#previewEngine?.updatePetName('');
    this.#setPreviewSide('front', false);
    this.#clearFeedback();
    this.#updateGroupCompleteStates();
    this.#refreshCurrentStepControls();
  }

  #refreshCarouselButtons() {
    for (const track of this.#carouselTracks) {
      this.#updateCarouselButtons(track);
    }
  }

  #pulsePreview() {
    const wrap = this.querySelector('.wroe-preview-wrap');
    if (!(wrap instanceof HTMLElement)) return;

    wrap.classList.add('is-updating');
    if (this.#previewPulseTimeout !== null) {
      window.clearTimeout(this.#previewPulseTimeout);
    }
    this.#previewPulseTimeout = window.setTimeout(() => {
      wrap.classList.remove('is-updating');
      this.#previewPulseTimeout = null;
    }, 260);
  }

  /**
   * @param {'front' | 'back'} side
   * @param {boolean} [animate=true]
   */
  #setPreviewSide(side, animate = true) {
    if (this.#previewSide === side && animate) return;
    this.#previewSide = side;

    const wrap = this.querySelector('.wroe-preview-wrap');
    const sideChip = /** @type {HTMLButtonElement} */ (this.refs.previewSideChip);
    if (sideChip) {
      sideChip.textContent = side === 'back' ? 'Back side' : 'Front side';
      sideChip.setAttribute('aria-label', side === 'back' ? 'Switch to front preview' : 'Switch to back preview');
    }

    if (this.#previewFlipSwapTimeout !== null) {
      window.clearTimeout(this.#previewFlipSwapTimeout);
      this.#previewFlipSwapTimeout = null;
    }
    if (this.#previewFlipEndTimeout !== null) {
      window.clearTimeout(this.#previewFlipEndTimeout);
      this.#previewFlipEndTimeout = null;
    }

    if (!(wrap instanceof HTMLElement) || !animate) {
      wrap?.classList.toggle('is-back', side === 'back');
      this.#previewEngine?.setSide?.(side);
      this.#pulsePreview();
      return;
    }

    wrap.classList.remove('is-flipping');
    void wrap.offsetWidth;
    wrap.classList.add('is-flipping');

    // Swap rendered side at 90deg so the flip reads as a true side change.
    this.#previewFlipSwapTimeout = window.setTimeout(() => {
      wrap.classList.toggle('is-back', side === 'back');
      this.#previewEngine?.setSide?.(side);
      this.#pulsePreview();
      this.#previewFlipSwapTimeout = null;
    }, 240);

    this.#previewFlipEndTimeout = window.setTimeout(() => {
      wrap.classList.remove('is-flipping');
      this.#previewFlipEndTimeout = null;
    }, 500);
  }

  /**
   * @param {HTMLElement} track
   */
  #updateCarouselButtons(track) {
    const wrap = track.closest('[data-wroe-carousel]');
    if (!(wrap instanceof HTMLElement)) return;

    const prev = wrap.querySelector('[data-wroe-carousel-prev]');
    const next = wrap.querySelector('[data-wroe-carousel-next]');
    if (!(prev instanceof HTMLButtonElement) || !(next instanceof HTMLButtonElement)) return;

    const max = track.scrollWidth - track.clientWidth;
    const canScroll = max > 4;
    wrap.classList.toggle('is-scrollable', canScroll);
    if (!canScroll) {
      prev.disabled = true;
      next.disabled = true;
      return;
    }

    prev.disabled = track.scrollLeft <= 2;
    next.disabled = track.scrollLeft >= max - 2;
  }

  /**
   * @param {Event} event
   */
  carouselPrev = (event) => {
    event.preventDefault();
    const target = event.target;
    if (!(target instanceof Element)) return;
    const wrap = target.closest('[data-wroe-carousel]');
    if (!(wrap instanceof HTMLElement)) return;
    const track = wrap.querySelector('[data-wroe-carousel-track]');
    if (!(track instanceof HTMLElement)) return;

    const amount = Math.max(180, Math.floor(track.clientWidth * 0.78));
    track.scrollBy({ left: -amount, behavior: 'smooth' });
    window.setTimeout(() => this.#updateCarouselButtons(track), 220);
  };

  /**
   * @param {Event} event
   */
  carouselNext = (event) => {
    event.preventDefault();
    const target = event.target;
    if (!(target instanceof Element)) return;
    const wrap = target.closest('[data-wroe-carousel]');
    if (!(wrap instanceof HTMLElement)) return;
    const track = wrap.querySelector('[data-wroe-carousel-track]');
    if (!(track instanceof HTMLElement)) return;

    const amount = Math.max(180, Math.floor(track.clientWidth * 0.78));
    track.scrollBy({ left: amount, behavior: 'smooth' });
    window.setTimeout(() => this.#updateCarouselButtons(track), 220);
  };

  get #hasColorOptions() {
    return this.dataset.hasColors === 'true';
  }

  /**
   * @param {string} field
   * @returns {boolean}
   */
  #isOptionEnabled(field) {
    switch (field) {
      case 'embroideryColor':
        return this.#hasColorOptions;
      case 'frontFabric':
        return this.#hasFrontFabricOptions;
      case 'backFabric':
        return this.#hasBackFabricOptions;
      default:
        return false;
    }
  }

  get #hasFrontFabricOptions() {
    return this.dataset.hasFrontFabrics === 'true';
  }

  get #hasBackFabricOptions() {
    return this.dataset.hasBackFabrics === 'true';
  }

  get #maxPetNameLength() {
    const value = Number(this.dataset.maxPetNameLength);
    return Number.isFinite(value) && value > 0 ? value : 12;
  }

  /** @returns {HTMLElement | null} */
  get #productForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const section = this.closest('.shopify-section');
    if (!section) return null;

    return section.querySelector(`product-form-component[data-product-id="${productId}"]`);
  }

  /** @returns {HTMLScriptElement | null} */
  get #stepsConfigElement() {
    const id = this.dataset.stepsConfigId;
    if (!id) return null;
    return /** @type {HTMLScriptElement | null} */ (document.getElementById(id));
  }

  /** @returns {HTMLScriptElement | null} */
  get #previewConfigElement() {
    const id = this.dataset.previewConfigId;
    if (!id) return null;
    return /** @type {HTMLScriptElement | null} */ (document.getElementById(id));
  }

  get #shareProductUrl() {
    const productPath = this.dataset.productUrl || '';
    if (!productPath) return window.location.href;

    try {
      return new URL(productPath, window.location.origin).toString();
    } catch {
      return window.location.href;
    }
  }
}

if (!customElements.get('wroe-personalization-component')) {
  customElements.define('wroe-personalization-component', WroePersonalizationComponent);
}
